import * as React from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import {
  ArrowDownFromLine,
  ArrowUp,
  ArrowUpToLine,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import { useT } from "../../../app/lib/i18n";
import { readNavCollapsed, writeNavCollapsed } from "../../../app/hooks/useSectionCollapse";
import { SectionDragHandleIcon } from "../CollapsibleSection/SectionDragHandleIcon";
import {
  SECTION_REORDER_CLICK_THRESHOLD_PX,
  SECTION_REORDER_ENTER_MS,
  applySectionReorder,
  resolveSectionInsertBefore,
  type SectionDropLine,
} from "../CollapsibleSection/sectionReorder";
import * as s from "./SectionNavigation.style.css";
import type { SectionNavigationProps } from "./SectionNavigation.types";

export type { SectionNavigationProps, SectionNavigationItem } from "./SectionNavigation.types";

const DRAG_IDX_ATTR = "data-nav-section-drag-idx";

type DragGhost = {
  clone: HTMLElement;
  w: number;
  h: number;
  sourceLeft: number;
};

function sanitizeNavClone(root: HTMLElement): void {
  root.removeAttribute(DRAG_IDX_ATTR);
  root.setAttribute("data-nav-section-drag-ghost", "");
  root.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));

  const handle = root.querySelector("[data-nav-section-drag-handle]") as HTMLElement | null;
  if (handle) {
    handle.style.opacity = "1";
    handle.style.pointerEvents = "none";
    handle.style.transform = "scale(1)";
    handle.style.transition = "none";
  }
}

export const SectionNavigation = ({
  sections,
  scrollContainer,
  heading = "Sections",
  persistCollapse = true,
  onExpandAll,
  onCollapseAll,
  areAllExpanded = false,
  onSectionReorder,
}: SectionNavigationProps) => {
  const t = useT();
  const [activeId, setActiveId] = React.useState<string>(sections[0]?.id ?? "");
  const [topOffset, setTopOffset] = React.useState(0);
  const [scrolled, setScrolled] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() =>
    persistCollapse ? readNavCollapsed() : false,
  );
  const sidebarRef = React.useRef<HTMLElement>(null);

  const sectionIds = React.useMemo(() => sections.map((sec) => sec.id), [sections]);
  const sectionIdsKey = sectionIds.join("\0");
  const sectionIdsRef = React.useRef(sectionIds);
  sectionIdsRef.current = sectionIds;
  const onReorderRef = React.useRef(onSectionReorder);
  onReorderRef.current = onSectionReorder;

  const showExpandCollapse =
    typeof onExpandAll === "function" && typeof onCollapseAll === "function";
  const sortable = typeof onSectionReorder === "function" && !collapsed && sections.length >= 2;

  const [dragSrcId, setDragSrcId] = React.useState<string | null>(null);
  const [dragSrcHover, setDragSrcHover] = React.useState(false);
  const [dropLine, setDropLine] = React.useState<SectionDropLine | null>(null);
  const [enteringIds, setEnteringIds] = React.useState(() => new Set<string>());

  const [dragGhost, setDragGhost] = React.useState<DragGhost | null>(null);
  const [dragGhostPos, setDragGhostPos] = React.useState({ x: 0, y: 0 });
  const dragGrabOffset = React.useRef({ x: 0, y: 0 });
  const pointerStart = React.useRef<{ x: number; y: number } | null>(null);
  const didDragRef = React.useRef(false);
  const [pointerTracking, setPointerTracking] = React.useState(false);
  const pendingDragRef = React.useRef<{ id: string; ghost: DragGhost } | null>(null);
  const ghostHostRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;
    const handler = () => setScrolled(container.scrollTop > 40);
    container.addEventListener("scroll", handler, { passive: true });
    return () => container.removeEventListener("scroll", handler);
  }, [scrollContainer]);

  React.useEffect(() => {
    const container = scrollContainer.current;
    if (!container || sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const id = entry.target.getAttribute("data-section-nav-id");
            if (id) setActiveId((prev) => (prev === id ? prev : id));
          }
        }
      },
      {
        root: container,
        rootMargin: "-10% 0px -80% 0px",
        threshold: 0,
      },
    );

    for (const section of sections) {
      const el = document.getElementById(section.id);
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIdsKey, scrollContainer]);

  React.useLayoutEffect(() => {
    if (sections.length === 0 || !sidebarRef.current) return;

    const measure = (): boolean => {
      const firstEl = document.getElementById(sections[0].id);
      const parentEl = sidebarRef.current?.parentElement;
      if (!firstEl || !parentEl) return false;
      const firstTop = firstEl.getBoundingClientRect().top;
      const parentTop = parentEl.getBoundingClientRect().top;
      const next = Math.max(0, Math.round(firstTop - parentTop));
      setTopOffset((prev) => (prev === next ? prev : next));
      return true;
    };

    if (measure()) return;

    // Sections may mount after nav items are registered (e.g. localization
    // waits on activeLang). Remeasure once the first section appears in the DOM.
    const observer = new MutationObserver(() => {
      if (measure()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIdsKey, collapsed]);

  React.useLayoutEffect(() => {
    const host = ghostHostRef.current;
    if (!host || !dragGhost) return;
    if (host.firstChild !== dragGhost.clone) {
      host.replaceChildren(dragGhost.clone);
    }
  }, [dragGhost]);

  React.useEffect(() => {
    if (!pointerTracking) return;

    const onPointerMove = (e: PointerEvent) => {
      const start = pointerStart.current;
      const pending = pendingDragRef.current;

      if (start && pending && !didDragRef.current) {
        const dist = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        if (dist < SECTION_REORDER_CLICK_THRESHOLD_PX) return;

        didDragRef.current = true;
        document.documentElement.style.setProperty("cursor", "grabbing", "important");
        document.body.style.userSelect = "none";
        setDragSrcId(pending.id);
        setDragGhost(pending.ghost);
        setDragGhostPos({ x: e.clientX, y: e.clientY });
      }

      if (!didDragRef.current) return;

      setDragGhostPos({ x: e.clientX, y: e.clientY });

      const ids = sectionIdsRef.current;
      const src = pending?.id ? ids.indexOf(pending.id) : -1;
      const { insertBefore, line } = resolveSectionInsertBefore(
        e.clientX,
        e.clientY,
        ids,
        DRAG_IDX_ATTR,
      );

      if (src >= 0 && (insertBefore === src || insertBefore === src + 1)) {
        setDragSrcHover(true);
        setDropLine(null);
      } else {
        setDragSrcHover(false);
        setDropLine(line);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      document.documentElement.style.removeProperty("cursor");
      document.body.style.userSelect = "";

      const pending = pendingDragRef.current;
      const srcId = pending?.id ?? null;
      const moved = didDragRef.current;

      setPointerTracking(false);
      pendingDragRef.current = null;
      setDragSrcId(null);
      setDragSrcHover(false);
      setDropLine(null);
      setDragGhost(null);
      pointerStart.current = null;
      didDragRef.current = false;

      if (!moved || !srcId) return;

      const ids = [...sectionIdsRef.current];
      const src = ids.indexOf(srcId);
      if (src < 0) return;

      const { insertBefore } = resolveSectionInsertBefore(
        e.clientX,
        e.clientY,
        ids,
        DRAG_IDX_ATTR,
      );
      const next = applySectionReorder(ids, src, insertBefore);
      if (!next) return;

      const at = next.indexOf(srcId);
      onReorderRef.current?.(next);

      requestAnimationFrame(() => {
        const lo = Math.min(src, at);
        const hi = Math.max(src, at);
        const movedIds = next.slice(lo, hi + 1);
        setEnteringIds((p) => {
          const set = new Set(p);
          movedIds.forEach((id) => set.add(id));
          return set;
        });
        window.setTimeout(() => {
          setEnteringIds((p) => {
            const set = new Set(p);
            movedIds.forEach((id) => set.delete(id));
            return set;
          });
        }, SECTION_REORDER_ENTER_MS);
      });
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
    };
  }, [pointerTracking]);

  function handleClick(id: string) {
    if (didDragRef.current) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleScrollTop() {
    scrollContainer.current?.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleExpandCollapseAll() {
    if (areAllExpanded) onCollapseAll?.();
    else onExpandAll?.();
  }

  function toggleCollapsed() {
    setCollapsed((prev) => {
      const next = !prev;
      if (persistCollapse) writeNavCollapsed(next);
      return next;
    });
  }

  const onHandlePointerDown = React.useCallback(
    (sectionId: string, e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();

      const row = (e.currentTarget as HTMLElement).closest(
        `[${DRAG_IDX_ATTR}]`,
      ) as HTMLElement | null;
      if (!row) return;

      const rect = row.getBoundingClientRect();
      dragGrabOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      pointerStart.current = { x: e.clientX, y: e.clientY };
      didDragRef.current = false;

      const clone = row.cloneNode(true) as HTMLElement;
      sanitizeNavClone(clone);
      clone.style.margin = "0";
      clone.style.width = "100%";
      clone.style.height = "100%";

      // Ghost should look like a normal hovered row with handle visible — strip
      // placeholder / insertion chrome that may exist on the live row.
      clone.querySelectorAll("[data-nav-drag-chrome]").forEach((el) => el.remove());

      pendingDragRef.current = {
        id: sectionId,
        ghost: {
          clone,
          w: Math.round(rect.width),
          h: Math.round(rect.height),
          sourceLeft: rect.left,
        },
      };
      setPointerTracking(true);
    },
    [],
  );

  if (sections.length < 2) return null;

  const topLabel = t("shared.ui.top");
  const expandCollapseLabel = areAllExpanded ? "Collapse all" : "Expand all";
  const dragActive = dragSrcId != null;

  const ghostLeft = dragGhost ? dragGhost.sourceLeft : 0;
  const ghostTop = dragGhost ? dragGhostPos.y - dragGrabOffset.current.y : 0;

  return (
    <nav
      ref={sidebarRef}
      className={clsx(s.sidebar, collapsed && s.sidebarCollapsed)}
      style={{ marginTop: topOffset }}
      aria-label={heading}
    >
      <div className={clsx(s.heading, collapsed && s.headingCollapsed)}>
        {!collapsed && <span className={s.headingLabel}>{heading}</span>}
        <div className={clsx(s.headingActions, collapsed && s.headingActionsCollapsed)}>
          <button
            type="button"
            className={s.iconBtn}
            onClick={toggleCollapsed}
            title={collapsed ? "Expand sections" : "Collapse sections"}
            aria-label={
              collapsed ? "Expand sections navigation" : "Collapse sections navigation"
            }
            aria-expanded={!collapsed}
          >
            {collapsed ? <PanelRightOpen size={14} /> : <PanelRightClose size={14} />}
          </button>
        </div>
      </div>

      {showExpandCollapse ? (
        <button
          type="button"
          className={clsx(s.item, collapsed && s.itemCollapsed)}
          onClick={handleExpandCollapseAll}
          title={expandCollapseLabel}
          aria-label={expandCollapseLabel}
        >
          <span className={s.itemIcon}>
            {areAllExpanded ? <ArrowUpToLine size={14} /> : <ArrowDownFromLine size={14} />}
          </span>
          {!collapsed && <span className={s.itemLabel}>{expandCollapseLabel}</span>}
        </button>
      ) : null}

      {sections.map((section, idx) => {
        const isDragSrc = dragSrcId === section.id;
        const isSrcHover = isDragSrc && dragSrcHover;
        const dropEdge =
          dropLine?.id === section.id && !isDragSrc ? dropLine.edge : null;
        const isEntering = enteringIds.has(section.id);

        return (
          <div
            key={section.id}
            className={clsx(
              s.itemRow,
              dropEdge === "before" && s.dropLineBefore,
              dropEdge === "after" && s.dropLineAfter,
              isEntering && s.itemEnter,
            )}
            {...(sortable ? { [DRAG_IDX_ATTR]: idx } : {})}
          >
            {isDragSrc ? (
              <div
                data-nav-drag-chrome=""
                className={clsx(
                  s.itemDraggingPlaceholder,
                  isSrcHover && s.itemDragSrcHover,
                )}
                aria-hidden
              />
            ) : null}

            <div
              className={clsx(
                s.itemInner,
                !dragActive && s.itemInnerHoverable,
                isDragSrc && s.itemInnerHidden,
                dragActive && !isDragSrc && s.itemInnerDragInactive,
              )}
            >
              <button
                type="button"
                className={clsx(
                  s.item,
                  sortable && s.itemWithHandle,
                  collapsed && s.itemCollapsed,
                  activeId === section.id && s.itemActive,
                )}
                onClick={() => handleClick(section.id)}
                title={collapsed ? section.label : undefined}
                aria-label={section.label}
              >
                {section.icon ? <span className={s.itemIcon}>{section.icon}</span> : null}
                {!collapsed && <span className={s.itemLabel}>{section.label}</span>}
              </button>

              {sortable ? (
                <button
                  type="button"
                  data-nav-section-drag-handle=""
                  className={clsx(s.itemHandle, isDragSrc && s.itemHandleRevealed)}
                  aria-label={`Reorder ${section.label}`}
                  onPointerDown={(e) => onHandlePointerDown(section.id, e)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <SectionDragHandleIcon box={14} variant="lines" />
                </button>
              ) : null}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className={clsx(s.item, collapsed && s.itemCollapsed)}
        onClick={handleScrollTop}
        title={topLabel}
        aria-label={t("shared.ui.scrollToTop")}
        style={{
          opacity: scrolled ? 1 : 0,
          pointerEvents: scrolled ? "auto" : "none",
        }}
      >
        <span className={s.itemIcon}>
          <ArrowUp size={14} />
        </span>
        {!collapsed && <span className={s.itemLabel}>{topLabel}</span>}
      </button>

      {dragGhost &&
        createPortal(
          <div
            ref={ghostHostRef}
            className={s.ghost}
            style={{
              left: ghostLeft,
              top: ghostTop,
              width: dragGhost.w,
              height: dragGhost.h,
              transform: "scale(1.03) rotate(1.5deg)",
              transformOrigin: `${dragGrabOffset.current.x}px ${dragGrabOffset.current.y}px`,
            }}
            aria-hidden
          />,
          document.body,
        )}
    </nav>
  );
};
